import { shallowRef, type InjectionKey, type Ref, type ShallowRef } from 'vue'

/**
 * 화면 이동 때 URL 쿼리 파라미터 대신 넘기는 값.
 * router.push({ ..., state }) 로 넘기면 이동한 history 항목(history.state)에 저장되어 URL 에 드러나지 않고,
 * 뒤로가기/앞으로가기/새로고침 때도 그 항목의 값이 그대로 남는다.
 */
export type NavState = {
  /** 처음 열 탭 (프로젝트, 데이터 상세, 워크스페이스, 관리) */
  tab?: string
  /** 워크스페이스에서 스크롤할 섹션 (pending-requests, my-requests) */
  focus?: string
  /** 프로젝트 랩노트에서 스크롤할 노트 번호 */
  noteId?: number
  /** DOI 관리 심사 상태 필터 */
  status?: string
  /** 프로젝트 목록 소유/참여 필터 */
  ownership?: string
  participation?: string
  /** 홈 검색어 */
  q?: string
  /** 홈 검색: 검색어 결합 방식, 필터, 더보기로 펼친 세션 수, 떠날 때의 스크롤 위치 */
  homeOperator?: 'or' | 'and'
  homeFilters?: Record<string, string[]>
  homeGroupCount?: number
  homeScrollTop?: number
  /** 떠날 때 메모리에 담아 둔 검색 결과(stores/modules/homeSearchCache.ts)의 id */
  homeSnapshotId?: number
  /** navOptions 로 이동한 시각. 같은 값으로 다시 이동해도 감시자가 반응하게 한다. */
  navAt?: number
}

/**
 * 본문 스크롤 영역(App.vue). 창(window)이 아니라 이 요소가 스크롤되어 vue-router 가 스크롤 위치를 저장하지 못하므로,
 * 돌아왔을 때 스크롤 위치를 되살려야 하는 화면이 inject 해서 쓴다.
 */
export const MAIN_SCROLL_KEY: InjectionKey<Ref<HTMLElement | null>> = Symbol('mainScroll')

function readHistoryState(): NavState {
  return { ...(window.history.state as NavState | null) }
}

const navState = shallowRef<NavState>(readHistoryState())

/** 현재 history 항목의 이동 값. 이동이 끝날 때마다(router.afterEach → syncNavState) 새 값으로 바뀐다. */
export function useNavState(): Readonly<ShallowRef<NavState>> {
  return navState
}

/** router.afterEach 에서 호출한다. 이동이 끝난 history 항목의 값을 반영한다. */
export function syncNavState(): void {
  navState.value = readHistoryState()
}

/**
 * 새 history 항목을 만들지 않고 현재 항목의 값만 바꾼다(예: 홈 검색어, 프로젝트 탭).
 * vue-router 가 쓰는 키(back, current, forward, position, scroll)는 그대로 둔다.
 * history.state 는 구조화 복제되어 반응형 Proxy 를 그대로 넣을 수 없으므로 평범한 값으로 복사해 넣는다.
 */
export function updateNavState(patch: NavState): void {
  window.history.replaceState({ ...window.history.state, ...JSON.parse(JSON.stringify(patch)) }, '')
  syncNavState()
}

/**
 * 알림처럼 "이 화면을 이 값으로 열어라" 하는 이동에 쓰는 옵션: router.push({ name, params, ...navOptions({ tab }) })
 * 이미 같은 화면에 있어도 이동하도록 force 를 켜고, 같은 값이어도 감시자가 반응하도록 navAt 을 붙인다.
 */
export function navOptions(state: NavState): { state: NavState; force: true } {
  return { state: { ...state, navAt: Date.now() }, force: true }
}

/**
 * 이동 뒤 화면이 직접 스크롤 위치를 정하는 경우(워크스페이스 섹션, 랩노트, 홈 검색 결과의 이전 스크롤 위치).
 * 이때는 App.vue 가 맨 위로 올리지 않는다.
 * scrollBehavior 와 다른 afterEach 에서도 등록 순서와 상관없이 쓰도록 history.state 를 직접 읽는다.
 */
export function hasFocusTarget(): boolean {
  const state = window.history.state as NavState | null
  return Boolean(state?.focus || state?.noteId || state?.homeScrollTop)
}
